import React, { Component } from 'react';
import {
    Panel,
    PanelType,
    PrimaryButton,
    DefaultButton,
    TextField,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType
} from '@fluentui/react';
import { IFeature, FeatureState } from './types';
import { createFeatureService, updateFeatureService } from './services';

interface IProps {
    isOpen: boolean;
    feature: IFeature | null;
    onDismiss: () => void;
    onSave: (feature: IFeature) => void;
}

interface IState {
    formData: IFeature;
    errorMessage: string;
    isSaving: boolean;
}

class FeatureEditPanel extends Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            formData: this._getInitialFormData(),
            errorMessage: '',
            isSaving: false
        };
    }

    componentDidUpdate(prevProps: IProps) {
        if (this.props.feature !== prevProps.feature || this.props.isOpen !== prevProps.isOpen) {
            this.setState({
                formData: this._getInitialFormData(),
                errorMessage: '',
                isSaving: false
            });
        }
    }

    private _getInitialFormData = (): IFeature => {
        if (this.props.feature) {
            return { ...this.props.feature };
        }
        return {
            id: 0,
            title: '',
            state: FeatureState.Disable,
            description: '',
            modifiedAt: new Date()
        };
    };

    private _onTitleChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
        this.setState({
            formData: { ...this.state.formData, title: newValue || '' },
            errorMessage: ''
        });
    };

    private _onDescriptionChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
        this.setState({
            formData: { ...this.state.formData, description: newValue || '' }
        });
    };

    private _onStateChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        if (option) {
            this.setState({
                formData: { ...this.state.formData, state: option.key as FeatureState }
            });
        }
    };

    private _onSave = async (): Promise<void> => {
        const { formData } = this.state;

        // Validation
        if (!formData.title?.trim()) {
            this.setState({ errorMessage: 'Feature title is required' });
            return;
        }

        if (!formData.description?.trim()) {
            this.setState({ errorMessage: 'Feature description is required' });
            return;
        }

        this.setState({ isSaving: true, errorMessage: '' });

        try {
            let savedFeature: IFeature;
            if (formData.id === 0) {
                // Create new feature
                const featureToCreate = {
                    title: formData.title,
                    state: formData.state,
                    description: formData.description
                };
                savedFeature = await createFeatureService(featureToCreate);
            } else {
                // Update existing feature - only send necessary fields
                const featureToUpdate = {
                    id: formData.id,
                    title: formData.title,
                    state: formData.state,
                    description: formData.description
                };
                savedFeature = await updateFeatureService(featureToUpdate);
            }

            this.props.onSave(savedFeature);
        } catch (error) {
            this.setState({
                errorMessage: 'Failed to save feature. Please try again.',
                isSaving: false
            });
        }
    };

    render() {
        const { isOpen, onDismiss, feature } = this.props;
        const { formData, errorMessage, isSaving } = this.state;

        const stateOptions: IDropdownOption[] = [
            {
                key: FeatureState.Disable,
                text: 'Disabled',
                data: { description: 'Feature is disabled for all users' }
            },
            {
                key: FeatureState.Enable,
                text: 'Enabled',
                data: { description: 'Feature is enabled for all users' }
            },
            {
                key: FeatureState.Beta,
                text: 'Beta',
                data: { description: 'Feature is available only for specified users (managed via permissions)' }
            }
        ];

        return (
            <Panel
                isOpen={isOpen}
                onDismiss={onDismiss}
                headerText={feature && feature.id !== 0 ? `Edit Feature: ${feature.title}` : 'Add New Feature'}
                closeButtonAriaLabel="Close"
                onRenderFooterContent={() => (
                    <div>
                        <PrimaryButton
                            onClick={this._onSave}
                            styles={{ root: { marginRight: 8 } }}
                            disabled={isSaving}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </PrimaryButton>
                        <DefaultButton onClick={onDismiss} disabled={isSaving}>
                            Cancel
                        </DefaultButton>
                    </div>
                )}
                isFooterAtBottom={true}
                customWidth='500px'
                type={PanelType.customNear}
                isLightDismiss={!isSaving}
            >
                {errorMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                        onDismiss={() => this.setState({ errorMessage: '' })}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: 15 } }}
                    >
                        {errorMessage}
                    </MessageBar>
                )}

                <TextField
                    label="Feature Title"
                    value={formData.title}
                    onChange={this._onTitleChange}
                    placeholder="Enter feature title (e.g., Advanced Search)"
                    required
                    styles={{ root: { marginBottom: 15 } }}
                />

                <Dropdown
                    label="State"
                    selectedKey={formData.state}
                    options={stateOptions}
                    onChange={this._onStateChange}
                    required
                    styles={{ root: { marginBottom: 15 } }}
                    onRenderOption={(option) => (
                        <div>
                            <div style={{ fontWeight: 600 }}>{option?.text}</div>
                            <div style={{ fontSize: '12px', color: '#605e5c' }}>
                                {option?.data?.description}
                            </div>
                        </div>
                    )}
                />

                {feature && feature.id !== 0 && (
                    <div style={{
                        padding: '12px',
                        backgroundColor: '#f3f2f1',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: '#605e5c'
                    }}>
                        <div><strong>Feature ID:</strong> {feature.id}</div>
                        <div><strong>Last Modified:</strong> {!!feature.modifiedAt ? new Date(feature.modifiedAt+'Z').toLocaleString() : ""}</div>
                    </div>
                )}
                <TextField
                    label="Description"
                    value={formData.description}
                    onChange={this._onDescriptionChange}
                    placeholder="Describe the feature and its purpose"
                    multiline
                    required
                    rows={10}
                    styles={{
                        root: { margin: '15px 0 15px 0'},
                        field: {
                            border: '1px solid #d1d1d1',
                            borderRadius: '2px',
                            padding: '8px',
                            minHeight: '200px'
                        },
                        fieldGroup: {
                            border: 'none',
                            '&::after': {
                                border: 'none'
                            }
                        }
                    }}
                />
            </Panel>
        );
    }
}

export default FeatureEditPanel;
